/**
 * parses a partner's `CustomAttributeDefinition` rows into flat per-attribute entries: safe-parses the
 * `definition` JSON blob, drops deleted attributes, and normalizes options/visibility. Does not dedup
 * or apply visibility gating — callers layer those on top.
 */
export function flattenCustomAttributeDefinitions(records) {
    const entries = [];
    for (const record of records) {
        const attributes = parseDefinitionAttributes(record.definition);
        if (!attributes) {
            continue;
        }
        for (const [label, entry] of Object.entries(attributes)) {
            if (entry.deleted === true) {
                continue;
            }
            const key = typeof entry.key === 'string' ? entry.key : undefined;
            if (!key || typeof entry.type !== 'string') {
                continue;
            }
            entries.push({
                key,
                label,
                type: entry.type,
                options: normalizeRawOptions(entry.options),
                defaultValue: entry.defaultValue,
                partnerId: record.partnerId,
                visibility: normalizeVisibility(entry.visibility),
            });
        }
    }
    return entries;
}
export function dedupeCustomAttributesByKey(entries, platformPartnerId) {
    const byKey = new Map();
    for (const entry of entries) {
        // a partner-owned definition wins over a platform one sharing the same key.
        if (entry.partnerId !== platformPartnerId || !byKey.has(entry.key)) {
            byKey.set(entry.key, entry);
        }
    }
    return byKey;
}
function parseDefinitionAttributes(definition) {
    try {
        const parsed = JSON.parse(definition);
        return parsed?.attributes ?? null;
    }
    catch {
        return null;
    }
}
function normalizeRawOptions(options) {
    if (!Array.isArray(options)) {
        return [];
    }
    return options
        .filter((option) => {
        const raw = option;
        return typeof raw?.key === 'string' && typeof raw?.value === 'string';
    })
        .flatMap((option) => expandLegacyCommaOptions(option));
}
/**
 * Splits a legacy "combined" option (whose value packs several comma/newline-separated labels into
 * a single row, e.g. `{ key: '1_2_3_4_5', value: '1, 2, 3, 4, 5' }`) into one option per label,
 * keyed by a slug of the label. Mirrors the portal grid/side-panel expansion so the same definition
 * yields the same options everywhere (display, filter, and the behavior read/write path). A
 * single-label option passes through unchanged.
 */
function expandLegacyCommaOptions(option) {
    const labels = option.value
        .split(/[\n,]/)
        .map((label) => label.trim())
        .filter((label) => label.length > 0);
    if (labels.length <= 1) {
        return [{ key: option.key, value: option.value }];
    }
    return buildOptionsFromLabels(labels);
}
export function buildOptionsFromLabels(labels) {
    const seenKeys = new Map();
    return labels.map((value) => {
        const base = slugify(value) || 'option';
        const occurrences = seenKeys.get(base) ?? 0;
        seenKeys.set(base, occurrences + 1);
        return { key: occurrences === 0 ? base : `${base}_${occurrences + 1}`, value };
    });
}
function slugify(label) {
    return label
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9 -]/g, '')
        .replace(/\s+/g, '_')
        .replace(/-+/g, '-');
}
function normalizeVisibility(visibility) {
    if (!visibility || typeof visibility !== 'object') {
        return { visibleToAllUsers: true, restrictedToUserGroups: [] };
    }
    const raw = visibility;
    return {
        visibleToAllUsers: raw.visibleToAllUsers === true,
        restrictedToUserGroups: Array.isArray(raw.restrictedToUserGroups)
            ? raw.restrictedToUserGroups.filter((id) => typeof id === 'string')
            : [],
    };
}
